/**
 * 유지보수 계약 API 클라이언트 (Phase 6-4 보강).
 * backend /maintenance/contracts/* + /analysis/maintenance/export.
 */

import type {
  ApiResponse,
  AuditEntry,
  MaintenanceContractCreateInput,
  MaintenanceContractDetailResponse,
  MaintenanceContractLookupRow,
  MaintenanceContractRow,
  MaintenanceContractUpdateInput,
  MaintenanceSubcontract,
  MaintenanceSubcontractInput,
  SubcontractPurchasesResponse,
} from './types';

const BASE = '/api';

export async function listContracts(year: number): Promise<{
  year: number;
  contracts: MaintenanceContractRow[];
}> {
  const res = await fetch(`${BASE}/maintenance/contracts?year=${year}`, {
    credentials: 'include',
  });
  const json = (await res.json()) as ApiResponse<{
    year: number;
    contracts: MaintenanceContractRow[];
  }>;
  if (!json.ok) throw new Error(json.error.message);
  return json.data;
}

/**
 * 출고등록 모달 전용 경량 계약 검색 (viewer 허용).
 * 식별 정보만 반환 — 금액/메타 노출 X.
 */
export async function listContractsLookup(): Promise<{
  contracts: MaintenanceContractLookupRow[];
}> {
  const res = await fetch(`${BASE}/maintenance/contracts/lookup`, {
    credentials: 'include',
  });
  const json = (await res.json()) as ApiResponse<{
    contracts: MaintenanceContractLookupRow[];
  }>;
  if (!json.ok) throw new Error(json.error.message);
  return json.data;
}

/** 유지보수 매출계산서 검색 — 매입 건명 연결용 (엔드유저/계약명으로 검색). */
export type MaintSaleOption = {
  maintenanceSalesId: number;
  contractId: number | null;
  endUser: string | null;
  year: number | null;
  month: number | null;
  invoiceDate: string | null;
  name: string;
};

export async function searchSalesOptions(q: string): Promise<MaintSaleOption[]> {
  const res = await fetch(
    `${BASE}/maintenance/sales-options?q=${encodeURIComponent(q)}`,
    { credentials: 'include' },
  );
  const json = (await res.json()) as ApiResponse<{ options: MaintSaleOption[] }>;
  if (!json.ok) throw new Error(json.error.message);
  return json.data.options;
}

export async function getContract(
  contractId: number,
  year: number,
): Promise<MaintenanceContractDetailResponse> {
  const res = await fetch(
    `${BASE}/maintenance/contracts/${contractId}?year=${year}`,
    { credentials: 'include' },
  );
  const json = (await res.json()) as ApiResponse<MaintenanceContractDetailResponse>;
  if (!json.ok) throw new Error(json.error.message);
  return json.data;
}

/**
 * 📜 계약 변경 이력 한 줄 (v2.279.0 · backend routes/maintenance/audit.ts) — 영업건·매입 이력(AuditEntry)과 같은 모양 +
 *   어느 표의 기록인지(tableName · 칸 이름표용) · 갈래(scope) · 「어느 것」(context: 「2026년 9월분」 · 「2026년 금액」 · 「하도급 …」).
 */
export interface MaintAuditEntry extends AuditEntry {
  tableName: string;
  scope: 'contract' | 'year' | 'subcontract' | 'month';
  context: string | null;
}

export async function getContractAudit(contractId: number): Promise<MaintAuditEntry[]> {
  const res = await fetch(`${BASE}/maintenance/contracts/${contractId}/audit`, { credentials: 'include' });
  const json = (await res.json()) as ApiResponse<MaintAuditEntry[]>;
  if (!json.ok) throw new Error(json.error.message);
  return json.data;
}

/** 엔드유저 이름을 바꿨을 때 예산 산정근거가 따라갔나 (v2.231.0 · D-11 · backend lib/maintenance/budget-follow-rename.ts) */
export interface BudgetFollowResult {
  followed: boolean;
  skipped?: 'same-name' | 'other-contracts' | 'too-long' | 'no-budget';
  conflicts: string[];
  movedBasis: number;
}

export async function updateContract(
  contractId: number,
  input: MaintenanceContractUpdateInput,
): Promise<{ budgetFollow: BudgetFollowResult | null }> {
  const res = await fetch(`${BASE}/maintenance/contracts/${contractId}`, {
    method: 'PATCH',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  const json = (await res.json()) as ApiResponse<{ contractId: number; budgetFollow?: BudgetFollowResult | null }>;
  if (!json.ok) throw new Error(json.error.message);
  return { budgetFollow: json.data.budgetFollow ?? null };
}

export async function createContract(
  input: MaintenanceContractCreateInput,
): Promise<number> {
  const res = await fetch(`${BASE}/maintenance/contracts`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  const json = (await res.json()) as ApiResponse<{ contractId: number }>;
  if (!json.ok) throw new Error(json.error.message);
  return json.data.contractId;
}

/** 계약 삭제 (admin 한정, 연결된 발행 내역 없을 때만 서버가 허용). */
export async function deleteContract(contractId: number): Promise<void> {
  const res = await fetch(`${BASE}/maintenance/contracts/${contractId}`, {
    method: 'DELETE',
    credentials: 'include',
  });
  const json = (await res.json()) as ApiResponse<{ contractId: number }>;
  if (!json.ok) throw new Error(json.error.message);
}

/**
 * 엑셀 다운로드 — 브라우저에서 Blob → 파일 저장 트리거.
 * 응답 헤더의 filename* 우선, 없으면 fallback.
 */
export async function downloadMaintenanceExcel(year: number): Promise<void> {
  const res = await fetch(`${BASE}/analysis/maintenance/export?year=${year}`, {
    credentials: 'include',
  });
  if (!res.ok) {
    let msg = `HTTP ${res.status}`;
    try {
      const json = (await res.json()) as { error?: { message?: string } };
      if (json?.error?.message) msg = json.error.message;
    } catch {
      /* not JSON */
    }
    throw new Error(msg);
  }

  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  // 응답 헤더의 Content-Disposition 에서 파일명 추출
  const cd = res.headers.get('Content-Disposition') ?? '';
  const utf8Match = cd.match(/filename\*=UTF-8''([^;]+)/i);
  const asciiMatch = cd.match(/filename="([^"]+)"/i);
  const name = utf8Match
    ? decodeURIComponent(utf8Match[1]!)
    : asciiMatch
    ? asciiMatch[1]!
    : `maintenance_${year}.xlsx`;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

// ─── 하도급 상세 (maintenance_subcontract) ───────────────────

export async function listSubcontracts(
  contractId: number,
  year: number,
): Promise<{ year: number; subcontracts: MaintenanceSubcontract[] }> {
  const res = await fetch(
    `${BASE}/maintenance/contracts/${contractId}/subcontracts?year=${year}`,
    { credentials: 'include' },
  );
  const json = (await res.json()) as ApiResponse<{
    year: number;
    subcontracts: MaintenanceSubcontract[];
  }>;
  if (!json.ok) throw new Error(json.error.message);
  return json.data;
}

export async function createSubcontract(
  contractId: number,
  input: MaintenanceSubcontractInput,
): Promise<number> {
  const res = await fetch(
    `${BASE}/maintenance/contracts/${contractId}/subcontracts`,
    {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    },
  );
  const json = (await res.json()) as ApiResponse<{ subcontractId: number }>;
  if (!json.ok) throw new Error(json.error.message);
  return json.data.subcontractId;
}

export async function updateSubcontract(
  subcontractId: number,
  input: Partial<MaintenanceSubcontractInput>,
): Promise<void> {
  const res = await fetch(`${BASE}/maintenance/subcontracts/${subcontractId}`, {
    method: 'PATCH',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  const json = (await res.json()) as ApiResponse<{ subcontractId: number }>;
  if (!json.ok) throw new Error(json.error.message);
}

export async function deleteSubcontract(subcontractId: number): Promise<void> {
  const res = await fetch(`${BASE}/maintenance/subcontracts/${subcontractId}`, {
    method: 'DELETE',
    credentials: 'include',
  });
  const json = (await res.json()) as ApiResponse<{ subcontractId: number }>;
  if (!json.ok) throw new Error(json.error.message);
}

/** 하도급 매입 현황 — 계약·연도의 하도급별 연결 매입(월별 주문/입고/출고/발행). */
export async function listSubcontractPurchases(
  contractId: number,
  year: number,
): Promise<SubcontractPurchasesResponse> {
  const res = await fetch(
    `${BASE}/maintenance/contracts/${contractId}/subcontract-purchases?year=${year}`,
    { credentials: 'include' },
  );
  const json = (await res.json()) as ApiResponse<SubcontractPurchasesResponse>;
  if (!json.ok) throw new Error(json.error.message);
  return json.data;
}

/* ── 예산 산정근거 (v2.56.0) ───────────────────────────────
   ① 고객사 리스트 → ② 납품내역 보기 → ③ 산정근거 만들기(불러오기 후 편집·저장) */

export type BudgetCategory = 'server' | 'storage' | 'software';

export const BUDGET_CATEGORY_LABEL: Record<BudgetCategory, string> = {
  server: '서버',
  storage: '스토리지',
  software: '소프트웨어',
};

export interface BudgetCustomer {
  name: string;
  /** 유지보수 계약 건수 (0 = 계약 없이 직접 추가한 곳) */
  contractCount: number;
  firstYear: number | null;
  lastYear: number | null;
  /** 영업진행에서 이 고객사로 볼 엔드유저 표기들 */
  aliases: string[];
  note: string | null;
  /** 계약에 없고 직접 추가한 고객사 */
  manual: boolean;
  /** 목록에서 제외한 곳 — 자료는 남아 있고 되돌릴 수 있다 */
  hidden: boolean;
  /** 별칭 포함 영업건 수 (전체) */
  salesCount: number;
  /**
   * 유지보수 대상 영업건 수 (v2.79.6) — 납품내역에서 실제로 살아남는 건수.
   * 목록의 **기본 정렬 기준**이기도 하다 (많은 곳부터).
   */
  validSalesCount: number;
  /** 위에서 뺀 내역 — 왜 줄었는지 화면 툴팁이 말해 준다 */
  salesExcluded: { unissued: number; excluded: number; service: number };
  /** 아직 연결 안 한 이름 후보 수 (v2.77.6) — 0 이면 🔗 을 눌러 볼 필요가 없다 */
  aliasCandidates: number;
  bases: {
    category: BudgetCategory;
    items: number;
    /** 도입금액 합계 */
    amount: number;
    /** 유지보수 요율(%) · null = 아직 안 정함 */
    ratePercent: number | null;
    /** 연간 유지보수료 = 도입금액 × 요율/100 · null = 요율 미정 */
    fee: number | null;
  }[];
  /** 세 분류 도입금액 합계 */
  totalAmount: number;
  /** 연간 유지보수료 합계 — 요율을 정한 분류만 더한다. 하나도 없으면 null */
  totalFee: number | null;
}

/** 영업건에 연결된 매입행 한 줄 — ⚠단가·금액은 서버가 절대 싣지 않는다 */
export interface BudgetDeliveryPurchase {
  group: string;
  /** 품목명 (item_master) — 납품내역 산정의 기준 */
  name: string;
  /** 규격 (Description) — 참고용 */
  spec: string;
  qty: number;
  /** 이 매입행이 산정에 반영된 표기 (null = 규칙 밖이라 무시) */
  mapped: string | null;
}

export interface BudgetDeliveryRow {
  /** 영업진행 행이면 영업번호, 과거자료 행이면 null */
  salesId: string | null;
  /** 과거자료(기초자료 엑셀) 행이면 번호, 영업진행 행이면 null */
  legacyId: number | null;
  /** 용도 (v2.77.4) — 사용자가 적는 것. 산정근거 자동채움에 따라간다 */
  usage: string | null;
  endUser: string | null;
  introDate: string | null;
  /** day | month | none — 과거자료는 년월만 아는 행이 있다 */
  datePrecision: string;
  deliveryDate: string | null;
  projectName: string | null;
  item: string | null;
  categories: BudgetCategory[];
  /** 장비가 아니라 용역(기술지원료) — 산정근거 자동 후보에서 빠진다 */
  service: boolean;
  /** 산정된 납품내역 — 매입 품목명 우선, 없으면 제안품목 문구 (규칙: upload 산정규칙 문서) */
  derived: string;
  /**
   * 산정 항목을 품목 단위로 쪼갠 것 (v2.79.2) — 화면에서 「항목 | 수량」 줄나눔 표기용.
   * ⚠derived 문자열을 다시 파싱하지 말 것 — 원문 유지 행은 왕복이 안 된다. 서버 엔진의 lines 그대로.
   * 빈 배열이면(원문 유지 등) derived 문자열로 폴백.
   * v2.79.5: 항목마다 **분류 하나**(null = 규칙 밖). 행이 항목별로 나뉘므로 배지도 항목 것으로.
   */
  /** v2.269.1: excluded = 「단가표 작성」에서 「넣지 않음」으로 정한 품목(산정근거 어디에도 안 들어감) */
  lines: { name: string; qty: number; category: BudgetCategory | null; excluded?: boolean }[];
  /** 매입연결 · 제안품목 · 제안품목(매입 규칙 미해당) · 용역(제외) · 수동 수정 · ― */
  method: string;
  derivedNote: string;
  purchases: BudgetDeliveryPurchase[];
  /**
   * 이 행에 걸린 수동 지시 (v2.77.3). null = 자동 산정 그대로.
   * edit 면 derived 가 이미 고친 값이고, autoDerived 에 원래 자동 산정값이 남는다.
   * exclude 면 화면에 회색으로 남고 합계·산정근거·단가표에서만 빠진다.
   */
  override: null | {
    action: 'edit' | 'exclude';
    reason: string;
    autoDerived: string;
    updatedBy: string | null;
    updatedAt: string | null;
  };
}

export interface BudgetBasisItem {
  itemId?: number | null;
  /** 용도 (v2.77.4) — 납품내역에서 실려 오고, 편집에서 고칠 수 있다 */
  usage?: string | null;
  introDate: string | null;
  projectName: string | null;
  item: string | null;
  capacity: string | null;
  amount: number | null;
  sourceSalesId: string | null;
  /** 단가표에 단가가 없어 금액에 못 넣은 품목 (서버가 알려 준다 · 저장되지는 않음) */
  unpriced?: string[];
  /**
   * 장비와 디스크가 한 행에 따로 적혀 금액을 두 번 셀 수 있는 경우의 장비 이름.
   * 사용자 결정(2026-08-10): 자동 보정하지 않고 ⚠ 경고만 — 사람이 판단한다.
   */
  doubleCount?: string[];
  /** 분류가 안 나와 이 분류에 안 넣은 항목 (v2.269.1 · 서버가 지금 기준으로 다시 봄) — 「단가표 작성」에서 분류를 정한다 */
  unclassified?: string[];
  /**
   * 최종 문서(엑셀) 품목 칸의 줄들 (v2.316.1 · 이은 품목만 v2.316.3) — SM 내부 명칭을 표준 표기(앞의 품명을 뺀 나라장터 명칭 · 「* N」)로.
   * std = 표준 표기로 바꾼 줄(아니면 쓴 글 그대로) · 저장된 품목 글 기준(고친 뒤엔 저장해야 다시 계산) · 옛 서버면 없다
   */
  docLines?: { text: string; std: boolean }[];
  /** 나라장터 명칭으로 바뀐 토막이 있나 — 없으면 📄 줄을 안 띄운다 */
  docConverted?: boolean;
  /** 표준 표기를 안 이어 SM 내부 명칭 그대로 문서에 나가는 품목 — 단가표·매칭 화면에서 이으면 바뀐다 */
  noStandard?: string[];
  /** 괄호·덧붙임이 있어 표준 표기로 못 바꾸는 토막 — 품목 글을 SM 내부 명칭으로 고쳐야 바뀐다 */
  unconvertible?: string[];
}

export interface BudgetItemPrice {
  priceId: number;
  label: string;
  matchKey: string;
  category: BudgetCategory | null;
  /** 1개당 도입금액(원). null = 아직 안 정함 */
  unitPrice: number | null;
  /** 1개당 용량(TB) — 이름에 TB 가 안 나오는 품목에만 쓴다 */
  capacityTb: number | null;
  /** g2b = 나라장터 등록상품에서 자동 생성 · manual = 직접 추가 */
  source: string;
  note: string | null;
  /** 유지보수 고객사 납품행에서 이 품목이 나온 횟수 */
  usageCount: number;
}

/** 납품내역에는 있는데 단가표에 없는 품목 */
export interface BudgetMissingItem {
  key: string;
  label: string;
  count: number;
}

export interface BudgetBasisResponse {
  customer: string;
  category: BudgetCategory;
  exists: boolean;
  basisId: number | null;
  note: string | null;
  updatedAt?: string;
  /** 유지보수 요율(%) · null = 아직 안 정함 */
  ratePercent: number | null;
  /** 도입금액 합계 (서버 계산) */
  totalAmount: number;
  /** 연간 유지보수료 · null = 요율 미정 */
  fee: number | null;
  items: BudgetBasisItem[];
}

/** 한 고객사의 세 분류 최종 산출 */
export interface BudgetSummary {
  customer: string;
  categories: {
    category: BudgetCategory;
    exists: boolean;
    rows: number;
    amount: number;
    ratePercent: number | null;
    fee: number | null;
    /** 단가를 모르는 품목이 있는 행 수 */
    unpricedRows: number;
    /** 장비+디스크가 한 행에 있는 행 수 (이중계산 확인 필요) */
    doubleCountRows: number;
    /** 분류 미정 항목이 있는 행 수 (v2.269.1) — 옛 서버면 없다 */
    unclassifiedRows?: number;
    /** 표준 표기(나라장터)로 안 적히는 품목이 있는 행 수 (v2.316.1 · 안 이음 + 글 모양 때문에 못 바꿈) · 옛 서버면 없다 */
    noStandardRows?: number;
  }[];
  totalAmount: number;
  totalFee: number | null;
}

/**
 * 유지보수료 = 도입금액 합계 × 요율(%) · 원 단위 반올림.
 *
 * ⚠ 계산의 주인은 서버(`lib/maintenance/classify.ts` 의 `maintenanceFee`)다.
 *   여기 있는 건 요율을 치는 동안 화면에 바로 보여 주기 위한 같은 식이다 —
 *   식을 고칠 일이 생기면 **두 곳 다** 고쳐야 한다.
 */
export function budgetFee(amount: number, ratePercent: number | null): number | null {
  if (ratePercent === null || !Number.isFinite(ratePercent)) return null;
  return Math.round((amount * ratePercent) / 100);
}

async function jget<T>(path: string): Promise<T> {
  const res = await fetch(`${BASE}${path}`, { credentials: 'include' });
  const json = (await res.json()) as ApiResponse<T>;
  if (!json.ok) throw new Error(json.error.message);
  return json.data;
}

/**
 * ⚠ **보낼 내용이 없으면 `Content-Type` 을 붙이지 않는다.**
 *   붙여 보내면 Fastify 가 라우트에 닿기도 전에
 *   "Body cannot be empty when content-type is set to 'application/json'" 400 으로 되돌려보낸다.
 *   그래서 본문 없는 DELETE 다섯 곳(↩ 되돌리기 · 고객사 ✕ · 산정근거 삭제 ·
 *   품목단가 삭제 · 납품단가 삭제)이 **눌러도 아무 일이 없었다**(오류창도 안 떴다).
 *   공용 `lib/api.ts` 는 처음부터 이렇게 되어 있다 — 이 파일만 어긋나 있었다. (v2.77.8)
 */
async function jsend<T>(path: string, method: string, body?: unknown): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    method,
    credentials: 'include',
    ...(body === undefined ? {} : { headers: { 'Content-Type': 'application/json' } }),
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  const json = (await res.json()) as ApiResponse<T>;
  if (!json.ok) throw new Error(json.error.message);
  return json.data;
}

export function listBudgetCustomers(
  includeHidden = false,
): Promise<{ customers: BudgetCustomer[]; hiddenCount: number }> {
  return jget(`/maintenance/budget/customers${includeHidden ? '?includeHidden=1' : ''}`);
}

/**
 * 목록에서 제외 / 되돌리기.
 * 고객사 목록은 계약에서 자동으로 나오므로 지울 수가 없다 — 깃발로만 뺀다(자료는 남는다).
 */
export function setBudgetCustomerHidden(
  name: string,
  hidden: boolean,
): Promise<{ name: string; hidden: boolean; basisCount: number }> {
  // ⚠고객사 이름은 주소 경로에 넣지 않는다 (v2.231.0 · D-10) — 운영 nginx 가 %2F 를 풀어 빗금 든 이름이 404 였다
  return jsend('/maintenance/budget/customers/hidden', 'POST', { name, hidden });
}

export function createBudgetCustomer(input: {
  name: string; aliases?: string[]; note?: string | null;
}): Promise<{ name: string }> {
  return jsend('/maintenance/budget/customers', 'POST', input);
}

export function updateBudgetCustomer(
  name: string,
  input: { aliases?: string[]; note?: string | null },
): Promise<{ name: string }> {
  return jsend('/maintenance/budget/customers', 'PATCH', { ...input, name });
}

export function deleteBudgetCustomer(name: string): Promise<{ deleted: boolean }> {
  return jsend(`/maintenance/budget/customers?name=${encodeURIComponent(name)}`, 'DELETE');
}

/** 고객사 납품항목별 합계 수량 — 유지보수 대상이 무엇을 몇 개 갖고 있는지 */
export interface BudgetDeliveryTotal {
  label: string;
  kind: 'body' | 'server' | 'disk';
  /** 규칙으로 정리된 표준 표기인가. false = 종류를 모르는 원문 */
  standard: boolean;
  qty: number;
  /** 이 항목이 나온 영업건 수 */
  rows: number;
}

export function listBudgetDeliveries(customer: string): Promise<{
  customer: string; matchedNames: string[]; legacyCount: number;
  /** 계산서 미발행이라 뺀 영업건 수 (v2.77.2) */
  unissuedCount: number;
  totals: BudgetDeliveryTotal[]; rows: BudgetDeliveryRow[];
}> {
  return jget(`/maintenance/budget/deliveries?customer=${encodeURIComponent(customer)}`);
}

/**
 * 수정 칸 입력(항목·수량) 미리보기 (v2.79.3) — 표준 표기 규칙을 통과하면 어떻게 정리되는지.
 * ⭐저장과 같은 함수를 태운다 — 미리보기 따로 저장 따로면 조용히 갈라진다.
 */
export function previewDeliveryOverrideItems(items: { name: string; qty: number }[]): Promise<{
  ok: boolean; derived: string; note: string; lines: { name: string; qty: number }[];
  /** 이름 한 칸에 수량·쉼표 표기가 섞인 의심 입력 안내 — 비어 있으면 정상 */
  warn: string;
}> {
  return jsend('/maintenance/budget/deliveries/override-preview', 'POST', { items });
}

/** 수동 수정/제외 저장 — 행 하나당 하나, 있으면 갈아끼운다. 사유 필수 */
export function saveDeliveryOverride(input: {
  salesId?: string | null;
  legacyId?: number | null;
  action: 'edit' | 'exclude';
  /** action='edit' 항목·수량 칸 (v2.79.3 구조화 수정) */
  items?: { name: string; qty: number }[];
  reason: string;
}): Promise<{ derived: string | null }> {
  return jsend('/maintenance/budget/deliveries/override', 'PUT', input);
}

/** 행 단위 용도 저장 — 빈 문자열이면 지운다 */
export function saveDeliveryUsage(input: {
  salesId?: string | null;
  legacyId?: number | null;
  usage: string;
}): Promise<{ usage: string | null }> {
  return jsend('/maintenance/budget/deliveries/usage', 'PUT', input);
}

/** 수동 지시 되돌리기 — 자동 산정으로 복귀 */
export function clearDeliveryOverride(key: {
  salesId?: string | null;
  legacyId?: number | null;
}): Promise<{ deleted: boolean }> {
  const q = key.salesId != null
    ? `salesId=${encodeURIComponent(key.salesId)}`
    : `legacyId=${key.legacyId}`;
  return jsend(`/maintenance/budget/deliveries/override?${q}`, 'DELETE');
}

export function listAliasCandidates(customer: string): Promise<{
  candidates: { name: string; salesCount: number }[];
}> {
  return jget(`/maintenance/budget/alias-candidates?customer=${encodeURIComponent(customer)}`);
}

export function getBudgetBasis(
  customer: string,
  category: BudgetCategory,
): Promise<BudgetBasisResponse> {
  return jget(
    `/maintenance/budget/basis?customer=${encodeURIComponent(customer)}&category=${category}`,
  );
}

export function createBudgetBasis(
  customer: string,
  category: BudgetCategory,
): Promise<{ basisId: number; rowCount: number }> {
  return jsend('/maintenance/budget/basis', 'POST', { customer, category });
}

export function saveBudgetBasis(
  basisId: number,
  input: { note?: string | null; ratePercent?: number | null; items: BudgetBasisItem[] },
): Promise<{ basisId: number; rowCount: number }> {
  return jsend(`/maintenance/budget/basis/${basisId}`, 'PUT', input);
}

/** 최종 산출 — 한 고객사의 세 분류 도입금액·요율·유지보수료 */
export function getBudgetSummary(customer: string): Promise<BudgetSummary> {
  return jget(`/maintenance/budget/summary?customer=${encodeURIComponent(customer)}`);
}

/**
 * 예산 산정근거 엑셀 받기.
 * `customer` 를 주면 그 고객사 한 곳(요약+분류별), 안 주면 전 고객사 총괄.
 */
export async function downloadBudgetExcel(customer?: string): Promise<void> {
  const qs = customer === undefined ? '' : `?customer=${encodeURIComponent(customer)}`;
  const res = await fetch(`${BASE}/maintenance/budget/export${qs}`, { credentials: 'include' });
  if (!res.ok) {
    let msg = `HTTP ${res.status}`;
    try {
      const json = (await res.json()) as { error?: { message?: string } };
      if (json?.error?.message) msg = json.error.message;
    } catch {
      /* not JSON */
    }
    throw new Error(msg);
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  const cd = res.headers.get('Content-Disposition') ?? '';
  const utf8Match = cd.match(/filename\*=UTF-8''([^;]+)/i);
  const asciiMatch = cd.match(/filename="([^"]+)"/i);
  a.download = utf8Match
    ? decodeURIComponent(utf8Match[1]!)
    : asciiMatch
    ? asciiMatch[1]!
    : 'budget_basis.xlsx';
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export function deleteBudgetBasis(basisId: number): Promise<{ deleted: boolean }> {
  return jsend(`/maintenance/budget/basis/${basisId}`, 'DELETE');
}

/**
 * 빈 금액·용량만 단가표로 채운다 (이미 값이 있는 칸은 안 건드림)
 *
 * ⚠ 본문이 필요 없는 POST 라도 `{}` 를 보내야 한다 — Content-Type 은 JSON 인데
 *   본문이 비면 서버가 400 으로 거절한다.
 */
export function refillBudgetBasis(
  basisId: number,
): Promise<{ basisId: number; filledAmount: number; filledCapacity: number }> {
  return jsend(`/maintenance/budget/basis/${basisId}/refill`, 'POST', {});
}

/* ── 품목 단가표 — 옛 문구 축 (v2.72.0 부터 화면에서 안 쓴다) ──
 *
 * 단가표 기준을 **산정 납품항목**으로 바꾸면서(사용자 결정 08-11) 문구 토막 목록 화면은
 * 없앴다. 라우트와 아래 함수는 남겨 둔다 — 산정근거 자동채움이 아직 이 축(axis='text')의
 * 단가를 쓰고 있고, 축을 갈아탈 때 다시 필요하다.
 */

export function listBudgetPrices(): Promise<{
  prices: BudgetItemPrice[]; missing: BudgetMissingItem[];
}> {
  return jget('/maintenance/budget/prices');
}

export function createBudgetPrice(input: {
  label: string;
  category?: BudgetCategory | null;
  unitPrice?: number | null;
  capacityTb?: number | null;
  note?: string | null;
}): Promise<{ priceId: number }> {
  return jsend('/maintenance/budget/prices', 'POST', input);
}

export function updateBudgetPrice(
  priceId: number,
  input: {
    label?: string;
    category?: BudgetCategory | null;
    unitPrice?: number | null;
    capacityTb?: number | null;
    note?: string | null;
  },
): Promise<{ priceId: number }> {
  return jsend(`/maintenance/budget/prices/${priceId}`, 'PATCH', input);
}

export function deleteBudgetPrice(priceId: number): Promise<{ deleted: boolean }> {
  return jsend(`/maintenance/budget/prices/${priceId}`, 'DELETE');
}

/* ── 납품항목 단가표 (v2.72.0) ───────────────────────── */

/** 산정 납품항목 한 종류 — 목록은 영업건에서 매번 다시 센 것이라 지울 수 없다 */
export interface DeliveryPriceItem {
  key: string;
  /** 산정된 납품 표기 (예: 'IFT 16TB') */
  label: string;
  kind: 'body' | 'server' | 'disk';
  /** 규칙으로 정리된 표준 표기인가. false = 제안품목 문구를 원문 그대로 둔 것 */
  standard: boolean;
  /** 이 품목이 나온 영업건 수 */
  rows: number;
  qty: number;
  fromPurchase: number;
  fromText: number;
  customers: string[];
  /**
   * 이 품목이 나온 건과 그 건의 수량 (v2.316.0 · 길이 = rows · 옛 서버면 없다).
   * key = `S:영업번호` · `L:과거자료 번호` — 이름·날짜는 listDeliveryPriceSources 의 같은 key
   */
  refs?: { key: string; qty: number }[];
  priceId: number | null;
  /** 1개당 도입금액(원). null = 아직 안 정함 (0원과 다르다) */
  unitPrice: number | null;
  capacityTb: number | null;
  /** 사람이 고른 분류 (v2.269.1) — none = 넣지 않음 · null = 자동(규칙) */
  category: BudgetCategory | 'none' | null;
  /** 규칙만으로 본 분류 — 「자동 (○○)」 · null 이면 사람이 골라야 산정근거에 들어간다 · 옛 서버면 없다 */
  autoCategory?: BudgetCategory | null;
  source: string | null;
  note: string | null;
  /** 값이 기존 [품목 단가표]에서 온 것 — 지우기는 그 화면에서 해야 한다 */
  fromTextTable: boolean;
  /** 이름에서 읽은 1개당 용량(TB). null 이 아니면 용량은 사람이 못 넣는다 */
  capacityFromName: number | null;
  /** 매입 품목명이 정리 안 된 것 (괄호·별표·쉼표·60자 초과) */
  nameWarn: boolean;
  /**
   * 표준 표기 = 나라장터 쇼핑몰 등록 명칭 (v2.316.1) — 산정근거 최종 문서(엑셀) 품목 칸에 이 SM 내부 명칭 대신 적힌다.
   * null = 안 이음(문서엔 SM 내부 명칭 그대로 + ⚠) · 옛 서버면 없다
   */
  standardName?: string | null;
  /** 그 명칭의 나라장터 물품식별번호 (목록에서 고른 것만) */
  standardG2bNo?: string | null;
  /**
   * 이을 때 본 SM 내부 명칭 — 문서는 이 이름과 **글자가 같은** 품목 글만 바꾼다.
   * label 과 글자가 다르면(같은 열쇠의 다른 표기 · 1.8TB ↔ 18TB) 지금 이름의 글은 안 바뀐다(화면이 알린다).
   */
  standardLabel?: string | null;
  /**
   * 이어 두면 문서에서 바뀌는 이름인가 — false = 쉼표·괄호가 들었거나 60자가 넘어 단가 찾기가 다르게 읽는 이름(이을 수 없음).
   * 옛 서버는 값이 없다(undefined = 이을 수 있음).
   */
  docConvertible?: boolean;
}

/** 표준 표기 후보 — 나라장터쇼핑몰 메뉴의 MAS 제품 풀네임 · 3자단가 등록 규격 (v2.316.1) */
export interface StandardNameCandidate {
  name: string;
  /** 나라장터 물품식별번호 */
  g2bNo: string | null;
  /** 나라장터 등록 가격(원) — 고를 때 참고(단가표 단가와 같으면 추천) */
  price: number | null;
  /** 'MAS 제품' | '3자단가' */
  source: string;
  /** MAS 는 분류(서버·디스크어레이) · 3자단가는 「선택부품 · 하드디스크드라이브」 */
  group: string;
}

export function listStandardNames(): Promise<{ names: StandardNameCandidate[] }> {
  return jget('/maintenance/budget/standard-names');
}

/**
 * 표준 표기 한 번에 잇기 (v2.316.1 · 「표준 표기 매칭」 [✔ 확실한 추천 한 번에 잇기]) — 한 트랜잭션(하나라도 틀리면 아무것도 안 바뀜).
 * standardName = null 이면 그 품목 연결 끊기.
 */
/** 안 이은 줄만 잇는다 — 그새 다른 곳에서 이어진 것은 skipped(그 SM 내부 명칭)로 돌아온다 */
export function saveStandardNames(items: {
  label: string; standardName: string; standardG2bNo?: string | null;
}[]): Promise<{ updated: number; created: number; skipped?: string[] }> {
  return jsend('/maintenance/budget/delivery-prices/standards', 'PUT', { items });
}

/** 단가표엔 있는데 지금 산정 목록엔 안 나오는 품목 */
export interface UnusedPriceItem {
  priceId: number;
  label: string;
  matchKey: string;
  unitPrice: number | null;
  source: string;
  note: string | null;
  axis: string;
  /**
   * 같은 영업건에서 실제로 같이 나온 표준 표기 — 같은 장비인지는 사람이 판단한다.
   * refs = 같이 나온 건의 key (v2.316.0 · 옛 서버면 없다)
   */
  suggest: { label: string; rows: number; refs?: string[] }[];
}

/**
 * 단가표 「영업건」 목록의 한 줄 (v2.316.0) — 어느 건인지 알아볼 것만(금액 없음).
 * 납품내역 보기 화면의 도입날짜·영업번호·프로젝트명과 같은 값이다.
 */
export interface DeliveryPriceSource {
  /** refs 의 key 와 같은 열쇠 — `S:영업번호` · `L:과거자료 번호` */
  key: string;
  /** null = 과거자료(기초자료 엑셀) 행 */
  salesId: string | null;
  legacyId: number | null;
  customer: string;
  introDate: string | null;
  /** 'month' 면 년월만 안다 */
  datePrecision: string;
  projectName: string | null;
  /** 매입연결 · 제안품목 · 제안품목(매입 규칙 미해당) · 과거자료 · 수동 수정 */
  method: string;
}

export interface DeliveryPriceSummary {
  salesRows: number;
  serviceRows: number;
  hiddenCustomers: number;
  /** 계산서 미발행이라 뺀 영업건 수 (v2.77.2) */
  unissuedRows: number;
  /** 수동 제외(v2.77.3)로 뺀 행 수 */
  manualExcluded: number;
  total: number;
  priced: number;
  unpriced: number;
  standardTotal: number;
  standardPriced: number;
  unusedPrices: number;
  /** 분류가 정해지지 않은 품목 수 (v2.269.1) — 산정근거에 「⚠ 분류 미정」으로 뜬다 */
  unclassifiedKinds?: number;
  /** 표준 표기(나라장터)를 이어 둔 품목 수 (v2.316.1 · 옛 서버면 없다) */
  standardLinked?: number;
}

export function listDeliveryPrices(): Promise<{
  items: DeliveryPriceItem[]; unused: UnusedPriceItem[]; summary: DeliveryPriceSummary;
}> {
  return jget('/maintenance/budget/delivery-prices');
}

/** 「영업건」 목록의 건 정보 (v2.316.0) — 숫자를 누를 때만 받는다(목록마다 실으면 응답이 세 배) */
export function listDeliveryPriceSources(): Promise<{ sources: DeliveryPriceSource[] }> {
  return jget('/maintenance/budget/delivery-prices/sources');
}

/** 산정 규칙 문서 한 항목 — 고정 표준 표기면 matchKey 로 단가를 이어 붙인다 */
export interface DeriveRuleEntry {
  from: string;
  to: string;
  priceLabel: string | null;
  matchKey: string | null;
}
export interface DeriveRuleSection {
  title: string;
  note: string | null;
  /** 'IFT' | 'EDS' — 용량 패턴 계열. 화면이 현재 목록에서 이 접두 디스크를 찾아 보여준다 */
  familyPrefix: string | null;
  entries: DeriveRuleEntry[];
}

/** 납품내역 산정 규칙 — 판정 엔진의 같은 표에서 나온다 (v2.77.5) */
export function getDeriveRules(): Promise<{ steps: string[]; sections: DeriveRuleSection[] }> {
  return jget('/maintenance/budget/derive-rules');
}

/** 표기 하나의 단가를 넣거나 고친다 (있으면 고치고 없으면 만든다) */
export function saveDeliveryPrice(input: {
  label: string;
  unitPrice?: number | null;
  capacityTb?: number | null;
  note?: string | null;
  /** 품목 분류 (v2.269.1) — none = 넣지 않음 · null = 자동(규칙) */
  category?: BudgetCategory | 'none' | null;
  /** 표준 표기(나라장터 쇼핑몰 등록 명칭 · v2.316.1) — null = 연결 끊기 */
  standardName?: string | null;
  /** 그 명칭의 나라장터 물품식별번호 — 목록에서 고르면 같이 · 직접 적으면 null */
  standardG2bNo?: string | null;
}): Promise<{ priceId: number; created: boolean }> {
  return jsend('/maintenance/budget/delivery-prices', 'PUT', input);
}

export function deleteDeliveryPrice(priceId: number): Promise<{ deleted: boolean }> {
  return jsend(`/maintenance/budget/delivery-prices/${priceId}`, 'DELETE');
}

export async function downloadDeliveryPriceExcel(): Promise<void> {
  const res = await fetch(`${BASE}/maintenance/budget/delivery-prices/export`, {
    credentials: 'include',
  });
  if (!res.ok) {
    let msg = `HTTP ${res.status}`;
    try {
      const json = (await res.json()) as { error?: { message?: string } };
      if (json?.error?.message) msg = json.error.message;
    } catch {
      /* not JSON */
    }
    throw new Error(msg);
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  const cd = res.headers.get('Content-Disposition') ?? '';
  const utf8Match = cd.match(/filename\*=UTF-8''([^;]+)/i);
  const asciiMatch = cd.match(/filename="([^"]+)"/i);
  a.download = utf8Match
    ? decodeURIComponent(utf8Match[1]!)
    : asciiMatch
    ? asciiMatch[1]!
    : 'delivery_prices.xlsx';
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
